import { Check, BadgeCheck } from "lucide-react";
import Seo from "@/components/Seo";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ChecklistForm from "@/components/site/ChecklistForm";
import { HEALTH_CHECK_PAGE, SITE } from "@/content/copy";
import { trackEvent } from "@/lib/analytics";

export default function HealthCheckPage() {
  return (
    <>
      <Seo
        title="Free Xero Health Check Checklist | Clarus Partners"
        description="Is your Xero file lodgement-ready? Get the free 15-point Xero Health Check Checklist a CPA uses before any clean-up. For trades businesses, startups and service businesses."
      />
      <Header />
      <main>
        <section data-testid="health-check-hero" className="bg-terracotta-light py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-sm font-bold uppercase tracking-widest text-terracotta">{HEALTH_CHECK_PAGE.overline}</p>
                <h1 className="mt-4 text-4xl sm:text-5xl tracking-tight font-extrabold text-navy">
                  {HEALTH_CHECK_PAGE.headline}
                </h1>
                <p className="mt-6 text-lg text-navy/70 leading-relaxed">{HEALTH_CHECK_PAGE.subheadline}</p>
                <div className="mt-8 flex items-center gap-3 text-sm font-semibold text-navy/70">
                  <BadgeCheck className="w-5 h-5 text-terracotta" aria-hidden="true" />
                  Prepared by Nima Riazati, CPA - 15+ years in finance
                </div>
              </div>
              <div className="rounded-2xl bg-white p-8 shadow-lg">
                <h2 className="text-xl font-semibold text-navy">Send me the checklist</h2>
                <div className="mt-5">
                  <ChecklistForm idPrefix="health-check-page" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-navy">{HEALTH_CHECK_PAGE.insideTitle}</h2>
              <ul className="mt-6 space-y-3">
                {HEALTH_CHECK_PAGE.inside.map((item) => (
                  <li key={item} className="flex gap-3 text-base text-navy/70 leading-relaxed">
                    <Check className="w-5 h-5 mt-0.5 shrink-0 text-terracotta" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-navy">{HEALTH_CHECK_PAGE.whoTitle}</h2>
              <ul className="mt-6 space-y-3">
                {HEALTH_CHECK_PAGE.who.map((item) => (
                  <li key={item} className="flex gap-3 text-base text-navy/70 leading-relaxed">
                    <Check className="w-5 h-5 mt-0.5 shrink-0 text-terracotta" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section data-testid="health-check-call-cta" className="py-20 bg-navy text-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold tracking-tight">{HEALTH_CHECK_PAGE.preferCall}</h2>
            <p className="mt-4 text-lg text-white/70">
              Skip the DIY - book a free 30-minute consultation and we'll tell you exactly what your file needs.
            </p>
            <a
              href={SITE.calendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="health-check-book-button"
              onClick={() => trackEvent("book_consultation_click", { location: "health_check_page" })}
              className="mt-8 inline-flex justify-center items-center rounded-full bg-terracotta px-8 py-4 text-base font-bold text-white transition-all duration-300 hover:bg-terracotta-hover hover:-translate-y-0.5 hover:shadow-lg"
            >
              {SITE.consultLabel}
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
